/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState } from 'react';
import { getISODateRange } from '../api/useInvoices';

export const TIME_RANGES = ['Recent', 'MTD', 'Month', 'YTD', 'YoY', 'Custom'] as const;
export type TimeRange = (typeof TIME_RANGES)[number];

interface PeriodFilterContextType {
  selectedRange: TimeRange;
  setSelectedRange: (range: TimeRange) => void;
  monthOffset: number;
  canGoNext: boolean;
  prevMonth: () => void;
  nextMonth: () => void;
  resetMonth: () => void;
  dateParams: { start_date?: string; end_date?: string };
  dateRangeDisplay: string;
}

const PeriodFilterContext = createContext<PeriodFilterContextType | undefined>(undefined);

const STORAGE_KEY = 'invoice_parse_selected_period';
const MONTH_OFFSET_STORAGE_KEY = 'invoice_parse_month_offset';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const MONTH_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export const PeriodFilterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedRange, setSelectedRangeState] = useState<TimeRange>(() => {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved && (TIME_RANGES as readonly string[]).includes(saved)) {
        return saved as TimeRange;
      }
    }
    return 'MTD';
  });

  // Clamp monthOffset so future months (offset > 0) are never selectable
  const [monthOffset, setMonthOffsetState] = useState<number>(() => {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const saved = localStorage.getItem(MONTH_OFFSET_STORAGE_KEY);
      if (saved) {
        const parsed = parseInt(saved, 10);
        if (!isNaN(parsed)) return Math.min(0, parsed);
      }
    }
    return 0;
  });

  const setSelectedRange = (range: TimeRange) => {
    setSelectedRangeState(range);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, range);
    }
  };

  const prevMonth = () => {
    setMonthOffsetState((prev) => {
      const updated = prev - 1;
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(MONTH_OFFSET_STORAGE_KEY, String(updated));
      }
      return updated;
    });
  };

  const nextMonth = () => {
    setMonthOffsetState((prev) => {
      if (prev >= 0) return 0; // Future months are blocked
      const updated = Math.min(0, prev + 1);
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(MONTH_OFFSET_STORAGE_KEY, String(updated));
      }
      return updated;
    });
  };

  const resetMonth = () => {
    setMonthOffsetState(0);
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(MONTH_OFFSET_STORAGE_KEY, '0');
    }
  };

  const canGoNext = monthOffset < 0;
  const dateParams = getISODateRange(selectedRange, monthOffset);

  // Compute readable date range label
  const now = new Date();
  const targetDate = new Date(now.getFullYear(), now.getMonth() + monthOffset, 1);
  const targetMonthName = MONTH_NAMES[targetDate.getMonth()];
  const targetMonthShort = MONTH_SHORT[targetDate.getMonth()];
  const targetYear = targetDate.getFullYear();
  const lastDayOfMonth = new Date(targetDate.getFullYear(), targetDate.getMonth() + 1, 0).getDate();

  let dateRangeDisplay = 'All Periods';
  if (selectedRange === 'Month') {
    dateRangeDisplay = `${targetMonthName} ${targetYear} (01 ${targetMonthShort} – ${lastDayOfMonth} ${targetMonthShort} ${targetYear})`;
  } else if (dateParams.start_date && dateParams.end_date) {
    dateRangeDisplay = `${dateParams.start_date} – ${dateParams.end_date}`;
  } else if (selectedRange === 'YTD') {
    dateRangeDisplay = `01 Jan – ${String(now.getDate()).padStart(2, '0')} ${MONTH_SHORT[now.getMonth()]} ${now.getFullYear()}`;
  } else if (selectedRange === 'YoY') {
    dateRangeDisplay = `01 Jan ${now.getFullYear() - 1} – ${String(now.getDate()).padStart(2, '0')} ${MONTH_SHORT[now.getMonth()]} ${now.getFullYear()}`;
  }

  return (
    <PeriodFilterContext.Provider
      value={{
        selectedRange,
        setSelectedRange,
        monthOffset,
        canGoNext,
        prevMonth,
        nextMonth,
        resetMonth,
        dateParams,
        dateRangeDisplay,
      }}
    >
      {children}
    </PeriodFilterContext.Provider>
  );
};

export function usePeriodFilter(): PeriodFilterContextType {
  const context = useContext(PeriodFilterContext);
  if (!context) {
    throw new Error('usePeriodFilter must be used within a PeriodFilterProvider');
  }
  return context;
}
